import {
  CellClassParams,
  CellClickedEvent,
  CellKeyDownEvent,
  CellStyle,
  ColDef,
  ColGroupDef,
  ColumnApi,
  EditableCallbackParams,
  GridApi,
  GridOptions,
  ICellRendererParams,
  ITooltipParams,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community'
import {
  ColumnType,
  columnTypes,
  dateValueFormatter,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import {
  CheckBoxCellRenderer,
  CustomEnumCellRenderer,
  DefaultCellRenderer,
} from '../../../containers/BulkSheetView/components/cellRenderer'
import _ from 'lodash'
import objects from '../../../../utils/objects'
import store from '../../../../store'
import { requireSave } from '../../../../store/requiredSaveData'
import { ServerSideUuidFilter } from '../../../containers/BulkSheetView/components/filter'
import { intl } from '../../../../i18n'
import {
  CheckBoxCellEditor,
  CustomEnumCellEditor,
} from '../../../containers/BulkSheetView/components/cellEditor'
import {
  ActualWorkingHoursRow,
  WorkingHoursRowIncludingCalculated,
  ActualWorkingHoursRowProjectBody,
  formatTime,
  formatWorkTime,
  WorkingHoursType,
  isWorkingDay,
  hasWorkingHours,
  isRequiredStartTime,
  isRequestCheckEditable,
  isAllRequestsApproved,
  RequestStatus,
} from '../ActualWorkingHours'
import { DISPLAY_DATE_SHORT_FORMAT_WITH_DAY } from '../../../../utils/date'
import './styles.scss'
import { ProjectDetail } from '../../../../lib/functions/project'
import { openTaskActualWorkListProps } from '../hooks/taskActualWorkTaskDialogState'
import { Color } from '../../../../styles/commonStyles'
import { featureFlagService } from '../../../../utils/feature-flag'
import DateVO from '../../../../vo/DateVO'
import { colorPalette } from '../../../style/colorPalette'
import TotalActualHourCellRenderer from '../components/CellRendere/TotalActualHourCellRenderer'
import RequestStatusCellRenderer from '../components/CellRendere/RequestStatusCellRenderer'
import { customHeaderTemplate } from '../../../containers/commons/AgGrid/components/header/CustomHeader'
import { workingProject } from '../../TaskActualWorkApprove/gridOptions'

export const actualWorkingHoursGridOptions = ({
  editable,
  openTaskActualWorkList,
  callbackCellEdit,
  isConfirm = false,
}: {
  editable: boolean
  openTaskActualWorkList?: (props: openTaskActualWorkListProps) => void
  callbackCellEdit: () => void
  isConfirm?: boolean
}): GridOptions => {
  return {
    groupHeaderHeight: 32,
    headerHeight: 45,
    rowHeight: 32,
    treeData: false,
    context: {},
    suppressLastEmptyLineOnPaste: true,
    tooltipShowDelay: 500,
    // Row
    rowDragManaged: false,
    rowDragMultiRow: false,
    suppressMoveWhenRowDragging: true,
    enterMovesDownAfterEdit: false,
    onCellValueChanged: event => {
      callbackCellEdit()
    },
    onCellKeyDown: (params: CellKeyDownEvent) => {
      // @ts-ignore
      if (!['Delete', 'Backspace'].includes(params.event.key)) return
      const cellRanges = params.api.getCellRanges() || []
      cellRanges.forEach(range => {
        const start = Math.min(range.startRow!.rowIndex, range.endRow!.rowIndex)
        const end = Math.max(range.startRow!.rowIndex, range.endRow!.rowIndex)
        range.columns.forEach(column => {
          const colDef = column.getColDef()
          if (
            typeof colDef.editable === 'function'
              ? colDef.editable(params)
              : colDef.editable
          ) {
            for (let i = start; i <= end; i++) {
              const rowNode = params.api.getDisplayedRowAtIndex(i)
              rowNode?.setDataValue(column, undefined)
            }
          }
        })
      })
    },
    // Column
    columnTypes: columnTypes(),
    components: frameworkComponents,
    defaultColDef: {
      width: 85,
      editable: (
        params: EditableCallbackParams<WorkingHoursRowIncludingCalculated>
      ) => {
        const data = params.data
        if (!data) return false
        if (data.type === 'SUM' || data.type === 'RATIO') return false
        return editable
      },
      enableValue: false,
      sortable: false,
      resizable: true,
      suppressMenu: true,
      suppressSizeToFit: true,
      singleClickEdit: false,
      cellEditor: 'textEditor',
      cellStyle: { justifyContent: 'center' },
      cellClassRules: {
        'sevend-ag-grid-date-header-holiday': params => {
          const types = [
            WorkingHoursType.PaidLeave,
            WorkingHoursType.PaidLeaveHalf,
            WorkingHoursType.SpecialLeave,
            WorkingHoursType.Holiday,
          ]
          const data = params.data
          if (data.type === 'SUM' || data.type === 'RATIO') {
            return false
          } else if (
            data.type === 'WORK_HOUR' &&
            (!!data.schedule?.legal ||
              data.schedule.workHour === 0 ||
              types.includes(data.workingHoursType))
          ) {
            return true
          }
          return false
        },
      },
      cellRenderer: DefaultCellRenderer,
      onCellClicked: defaultOnCellClicked,
      suppressPaste: params => !_.isEmpty(params.context?.copied),
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return (
          !params.editing && ['Delete', 'Backspace'].includes(params.event.key)
        )
      },
      valueSetter: (params: ValueSetterParams) => {
        const field = params.colDef.field || params.colDef.colId
        if (
          !field ||
          (!params.oldValue && !params.newValue) ||
          params.oldValue === params.newValue
        ) {
          return false
        }
        let newValue = params.newValue
        if (
          ['actual.startTime', 'actual.endTime', 'actual.breakTime'].includes(
            field
          )
        ) {
          if (!!params.newValue) {
            if (!validateTime(params.newValue)) {
              return false
            }
            newValue = parseTimeFormat(params.newValue)
          }
        }
        objects.setValue(params.data, field, newValue)
        params.data.edited = true
        store.dispatch(requireSave())
        return true
      },
    },
    columnDefs: [
      {
        field: 'uuid',
        hide: true,
        suppressColumnsToolPanel: true,
        filter: ServerSideUuidFilter,
      },
      {
        colId: 'blank',
        hide: true,
        width: 35,
        suppressColumnsToolPanel: true,
      },
      {
        field: 'date',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.date' }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 90,
        editable: false,
        type: [ColumnType.date],
        valueFormatter: (params: ValueFormatterParams) =>
          dateValueFormatter(
            params.value,
            DISPLAY_DATE_SHORT_FORMAT_WITH_DAY
          ) || '',
      },
      {
        field: 'workingHoursType',
        headerName: intl.formatMessage({
          id: 'actualWorkingHours.workingHoursType',
        }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 140,
        type: [ColumnType.customEnum],
        cellEditor: CustomEnumCellEditor,
        cellEditorParams: {
          customEnumCode: 'workingHoursType',
        },
        cellRenderer: CustomEnumCellRenderer,
        cellRendererParams: {
          customEnumCode: 'workingHoursType',
        },
        cellClass: 'workingHoursType',
      },
      {
        field: 'actual.startTime',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.startTime' }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 100,
        cellClass: 'actualTime',
        cellClassRules: {
          'actual-hours-required-input': params => {
            const data = params.data
            if (data.type === 'SUM' || !data.actual) return false
            return isRequiredOtherSide(
              data.actual.endTime,
              data.actual.startTime
            )
          },
          'actual-hours-required-start-time': params => {
            return isRequiredStartTime(params.data)
          },
        },
      },
      {
        field: 'actual.endTime',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.endTime' }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 100,
        cellClass: 'actualTime',
        cellClassRules: {
          'actual-hours-required-input': params => {
            const data = params.data
            if (data.type === 'SUM' || !data.actual) return false
            return isRequiredOtherSide(
              data.actual.startTime,
              data.actual.endTime
            )
          },
        },
      },
      {
        field: 'actual.breakTime',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.breakTime' }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 100,
        valueGetter: (
          params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
        ) => {
          const data = params.data
          if (!data) return undefined
          if (data.type === 'SUM') {
            return data.breakTime ? formatTime(data.breakTime) : ''
          } else if (data.type === 'WORK_HOUR') {
            return data.actual?.breakTime ?? ''
          }
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value
        },
        cellClass: (
          params: CellClassParams<WorkingHoursRowIncludingCalculated>
        ) => {
          const data = params.data
          if (!data) return ''
          if (data.type === 'SUM') {
            return 'timeFormula'
          } else if (data.type === 'WORK_HOUR') {
            return 'breakTime'
          }
        },
      },
      {
        field: 'actual.workedTime',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.workedTime' }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 90,
        editable: false,
        valueGetter: (
          params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
        ) => {
          const data = params.data
          if (!data) return undefined
          if (data.type === 'SUM') {
            return formatTime(data.workHour)
          } else if (data.type === 'WORK_HOUR') {
            return formatWorkTime(data)
          }
        },
        cellClass: 'timeFormula',
      },
      {
        field: 'remarks',
        headerName: intl.formatMessage({ id: 'actualWorkingHours.remarks' }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 200,
        cellStyle: { justifyContent: 'start' },
      },
      isConfirm ? undefined : requestDynamicColumnDef(),
      {
        field: 'totalActualHour',
        headerName: intl.formatMessage({
          id: 'actualWorkingHours.actualHour',
        }),
        hide: false,
        pinned: true,
        lockPosition: 'left',
        width: 85,
        editable: false,
        valueGetter: (
          params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
        ) => {
          const data = params.data
          if (!data) return undefined
          if (data.type === 'RATIO') {
            return []
          } else {
            const value = data.totalActualHour || 0
            if (value === 0) return ''
            return formatTime(value)
          }
        },
        cellClass: 'actualTime',
        cellRenderer: TotalActualHourCellRenderer,
        cellStyle: featureFlagService.isEnabled('task-actual-work-application')
          ? params => {
              const data = params.data as ActualWorkingHoursRow
              if (!data || data.type !== 'WORK_HOUR') return undefined

              const formatTotalProjectHours = formatTime(
                data.projectHours?.reduce(
                  (sum, project) => sum + (project.actualHour || 0),
                  0
                ) || 0
              )
              const actualHour = formatWorkTime(data)
              const today = new DateVO().formatForApi()
              let discrepancyStyle = {}
              if (data.date && today && data.date <= today) {
                if (isWorkingDay(data.workingHoursType as WorkingHoursType)) {
                  if (formatTotalProjectHours === '0:00' && actualHour === '') {
                    return
                  }
                  if (formatTotalProjectHours !== actualHour) {
                    discrepancyStyle = DISCREPANCY_CELL_STYLE
                  }
                }
              }
              const clickableStyle = getActualWorkColumnCellStyle(() => {
                return totalActualHourColumnClickable(data)
              })
              return {
                ...clickableStyle,
                ...discrepancyStyle,
              }
            }
          : (params: CellClassParams) => {
              return getActualWorkColumnCellStyle(() => {
                return totalActualHourColumnClickable(params.data)
              })
            },
        onCellClicked: (event: CellClickedEvent) => {
          if (!totalActualHourColumnClickable(event.data)) {
            return
          }
          onActualWorkCellClicked(
            event,
            event.context.userUuid,
            openTaskActualWorkList,
            editable
          )
        },
      },
    ].filter((def): def is ColDef<any> | ColGroupDef<any> => def !== undefined),
    excelStyles: [
      {
        id: 'dateType',
        dataType: 'DateTime',
        numberFormat: {
          format: intl.formatMessage({
            id: 'actualWorkingHours.excel.date.format',
          }),
        },
      },
      {
        id: 'actualTime',
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'breakTime',
        numberFormat: {
          format: intl.formatMessage({
            id: 'h:mm',
          }),
        },
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'workingHoursType',
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'timeFormula',
        dataType: 'Formula',
        numberFormat: {
          format: intl.formatMessage({
            id: '[h]:mm',
          }),
        },
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'centerCell',
        alignment: {
          horizontal: 'Center',
        },
      },
      {
        id: 'justifyRight',
        alignment: {
          horizontal: 'Right',
        },
      },
      {
        id: 'formulaTimeCell',
        numberFormat: {
          format: '[h]:mm',
        },
        alignment: {
          horizontal: 'Right',
        },
      },
      {
        id: 'formulaTimeCell',
        dataType: 'Formula',
      },
      {
        id: 'formulaCell',
        dataType: 'Formula',
      },
    ],
  }
}

const validateTime = (value: string): boolean => {
  if (!value) return true
  if (
    /^([01０１]?[0-9０-９]|[2２][0-3０-３])([:：])([0-5０-５][0-9０-９])$/.test(
      value
    )
  ) {
    return true
  }
  return /^([01０１]?[0-9０-９]|[2２][0-3０-３])([0-5０-５][0-9０-９])$/.test(
    value
  )
}

const isRequiredOtherSide = (base: string, target: string): boolean => {
  return !!base && !target
}

const COLUMN_FIELD_PREFIX_PROJECT: string = 'project_'

export const DISCREPANCY_CELL_STYLE = {
  background: colorPalette.pink[1],
  transition: 'none',
  '--ag-row-hover-color': colorPalette.pink[1],
  '--ag-selected-row-background-color': colorPalette.pink[1],
  '--ag-row-focus-color': colorPalette.pink[1],
  '--ag-cell-focus-border': 'none',
  '--ag-range-selection-background-color': colorPalette.pink[1],
  '--ag-range-selection-highlight-color': colorPalette.pink[1],
  '--ag-cell-horizontal-border': 'none',
  '--ag-cell-vertical-border': 'none',
}

export const calculateRatio = (
  numerator: number,
  denominator: number
): number => {
  if (denominator === 0) return 0
  return Math.round((numerator / denominator) * 1000) / 10
}

export type RefreshDynamicColumnDefProps = {
  api: GridApi | null | undefined
  projects: ProjectDetail[]
  userUuid: string | undefined
  openTaskActualWorkList?: (props: openTaskActualWorkListProps) => void
  isConfirm?: boolean
  data?: ActualWorkingHoursRow[]
}

const requestDynamicColumnDef = (): ColDef<any> | undefined => {
  if (featureFlagService.isEnabled('task-actual-work-application')) {
    return {
      field: 'requestCheck',
      headerName: intl.formatMessage({
        id: 'actualWorkingHours.request',
      }),
      headerTooltip: intl.formatMessage({
        id: 'actualWorkingHours.request.description',
      }),
      headerComponentParams: {
        template: customHeaderTemplate({
          tooltip: intl.formatMessage({
            id: 'actualWorkingHours.request.description',
          }),
        }),
      },
      width: 120,
      hide: false,
      pinned: true,
      lockPosition: 'left',
      type: [ColumnType.checkBox],
      cellEditor: CheckBoxCellEditor,
      editable: (
        params: EditableCallbackParams<WorkingHoursRowIncludingCalculated>
      ) => {
        const data = params.data
        if (!data) return false
        if (data.type === 'SUM' || data.type === 'RATIO') return false
        if (data.type === 'WORK_HOUR') {
          return isRequestCheckEditable(data)
        }
        return false
      },
      cellRendererSelector: (
        params: ICellRendererParams<WorkingHoursRowIncludingCalculated>
      ) => {
        const data = params.data
        if (!data) return { component: DefaultCellRenderer }
        if (data.type === 'SUM' || data.type === 'RATIO') {
          return { component: DefaultCellRenderer }
        }
        if (data.type === 'WORK_HOUR') {
          if (isWorkingDay(data.workingHoursType as WorkingHoursType)) {
            if (isAllRequestsApproved(data)) {
              params.context.checkboxDisabled = true
              return {
                component: CheckBoxCellRenderer,
                params: {
                  enableDisabledByContext: true,
                },
              }
            }
            if (!hasWorkingHours(data)) {
              params.context.checkboxDisabled = true
              return {
                component: CheckBoxCellRenderer,
                params: {
                  enableDisabledByContext: true,
                },
              }
            }
            return { component: CheckBoxCellRenderer }
          }
          return { component: DefaultCellRenderer }
        }
      },
      valueFormatter: (params: ValueFormatterParams) => {
        const data = params.data
        return isWorkingDay(data.workingHoursType as WorkingHoursType)
          ? params.value === undefined
            ? undefined
            : params.value.toString()
          : ''
      },
      keyCreator: (params): string => {
        return params.value ? 'checked' : ''
      },
      tooltipValueGetter: (
        params: ITooltipParams<WorkingHoursRowIncludingCalculated>
      ) => {
        const data = params.data
        if (!data) return ''
        if (data.type !== 'WORK_HOUR') return ''
        const workHourData = data
        if (!isWorkingDay(workHourData.workingHoursType as WorkingHoursType)) {
          return ''
        }
        if (!hasWorkingHours(workHourData)) {
          return intl.formatMessage({
            id: 'actualWorkingHours.requestCheck.noWorkingHours',
          })
        }
        return ''
      },
    }
  } else {
    return undefined
  }
}

export const refreshDynamicColumnDef = ({
  api,
  projects,
  userUuid,
  openTaskActualWorkList,
  isConfirm = false,
  data,
}: RefreshDynamicColumnDefProps) => {
  if (!api || _.isEmpty(projects)) return
  const columnDefs = api.getColumnDefs()
  const newColumnDefs = columnDefs?.filter(
    def =>
      !('field' in def) || !def.field?.startsWith(COLUMN_FIELD_PREFIX_PROJECT)
  )
  const currentProject = store.getState().project?.current?.uuid
  const displayProjects = workingProject(data || [], projects, currentProject)
  displayProjects.forEach(project => {
    if (
      featureFlagService.isEnabled('task-actual-work-application') &&
      !isConfirm
    ) {
      newColumnDefs?.push({
        headerName: project.displayName,
        field: COLUMN_FIELD_PREFIX_PROJECT + project.uuid,
        children: [
          {
            field:
              COLUMN_FIELD_PREFIX_PROJECT + project.uuid + '_requestStatus',
            headerName: intl.formatMessage({
              id: 'actualWorkingHours.requestStatus',
            }),
            hide: false,
            pinned: false,
            editable: false,
            width: 120,
            refData: { uuid: project.uuid, code: project.code },
            cellRenderer: RequestStatusCellRenderer,
            valueGetter: (
              params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
            ) => {
              const uuid = params.colDef.refData?.uuid
              const data = params.data
              if (!data) return undefined
              if (data.type === 'WORK_HOUR') {
                const targetProjectHour = data.projectRequestStatus?.find(
                  p => p.projectUuid === uuid
                )
                const requestStatus =
                  targetProjectHour?.requestStatus || undefined
                if (requestStatus === undefined) return ''
                return intl.formatMessage({
                  id:
                    'actualWorkingHours.requestStatus.' +
                    requestStatus.valueOf().toString().toLowerCase(),
                })
              } else if (data.type === 'RATIO') {
                const targetProjectRequest = data.projectRequestStatus?.find(
                  p => p.projectUuid === uuid
                )
                return targetProjectRequest?.approvedStatusConunt ===
                  undefined &&
                  targetProjectRequest?.requestStatusConunt === undefined
                  ? ''
                  : targetProjectRequest?.approvedStatusConunt +
                      '/' +
                      targetProjectRequest?.requestStatusConunt
              }
            },
            valueFormatter: (params: ValueFormatterParams) => params.value,
          },
          {
            field: COLUMN_FIELD_PREFIX_PROJECT + project.uuid + '_actualHour',
            headerName: intl.formatMessage({
              id: 'actualWorkingHours.actualHour',
            }),
            hide: true,
            pinned: false,
            editable: false,
            refData: { uuid: project.uuid, code: project.code },
            valueGetter: (
              params: ValueGetterParams<WorkingHoursRowIncludingCalculated>
            ) => {
              const uuid = params.colDef.refData?.uuid
              const data = params.data
              if (!data) return undefined
              const targetProjectHour = data.projectHours?.find(
                p => p.projectUuid === uuid
              )
              if (data.type === 'RATIO') {
                const totalActualHour = data.totalActualHour || 0
                if (totalActualHour === 0) return []
                return (
                  calculateRatio(
                    targetProjectHour?.actualHour ?? 0,
                    totalActualHour
                  ) + '%'
                )
              }
              const hour = targetProjectHour?.actualHour || 0
              if (hour === 0) return ''
              return formatTime(hour)
            },
            valueFormatter: (params: ValueFormatterParams) => params.value,
            cellStyle: (params: CellClassParams) => {
              return getActualWorkColumnCellStyle(() => {
                return projectColumnClickable(
                  params.data,
                  params.colDef.refData?.uuid
                )
              })
            },
            onCellClicked: (event: CellClickedEvent) => {
              if (
                !projectColumnClickable(event.data, event.colDef.refData?.uuid)
              ) {
                return
              }
              onActualWorkCellClicked(event, userUuid, openTaskActualWorkList)
            },
            cellClass: 'actualTime',
            width: 125,
          },
        ],
      })
    } else {
      newColumnDefs?.push({
        headerName: project.displayName,
        field: COLUMN_FIELD_PREFIX_PROJECT + project.uuid,
        hide: true,
        pinned: false,
        editable: false,
        refData: { uuid: project.uuid, code: project.code },
        valueGetter: (params: ValueGetterParams) => {
          const uuid = params.colDef.refData?.uuid
          const targetProjectHour: ActualWorkingHoursRowProjectBody =
            params.data.projectHours?.find(p => p.projectUuid === uuid)
          const hour = targetProjectHour?.actualHour || 0
          if (hour === 0) return ''
          return formatTime(hour)
        },
        valueFormatter: (params: ValueFormatterParams) => params.value,
        cellStyle: (params: CellClassParams) => {
          return getActualWorkColumnCellStyle(() => {
            return projectColumnClickable(
              params.data,
              params.colDef.refData?.uuid
            )
          })
        },
        onCellClicked: (event: CellClickedEvent) => {
          if (!projectColumnClickable(event.data, event.colDef.refData?.uuid)) {
            return
          }
          onActualWorkCellClicked(event, userUuid, openTaskActualWorkList)
        },
        cellClass: 'actualTime',
        width: 125,
      })
    }
  })
  api.setColumnDefs(newColumnDefs!)
}

export const updateDisplayProjectColumn = (
  columnApi: ColumnApi | null | undefined,
  gridApi: GridApi | null | undefined,
  data: ActualWorkingHoursRow[]
) => {
  if (!columnApi || !gridApi || _.isEmpty(data)) return

  const displayProjectUuids = new Set<string>(
    data
      .filter(d => !!d.projectHours)
      .flatMap(d => d.projectHours)
      .map(p => p?.projectUuid)
      .filter(p => !!p) as string[]
  )

  const columnState = columnApi.getColumnState()
  columnState.forEach(state => {
    if (!state.colId.startsWith(COLUMN_FIELD_PREFIX_PROJECT)) return

    const uuidWithSuffix = state.colId.slice(COLUMN_FIELD_PREFIX_PROJECT.length)
    const uuid = uuidWithSuffix.split('_')[0]
    state.hide = !displayProjectUuids.has(uuid)
  })
  columnApi.applyColumnState({
    state: columnState,
    applyOrder: true,
  })

  const columnDefs: (ColDef | ColGroupDef)[] = gridApi.getColumnDefs() || []
  const visibleColDefs = columnDefs.filter(def => {
    if ('children' in def && Array.isArray(def.children)) return true
    if (!('colId' in def) || !def?.colId) return false
    if (!def.colId.startsWith(COLUMN_FIELD_PREFIX_PROJECT)) return true
    const uuid = def.colId.slice(COLUMN_FIELD_PREFIX_PROJECT.length)
    return displayProjectUuids.has(uuid)
  })
  gridApi.getToolPanelInstance('columns')?.setColumnLayout(visibleColDefs)
}

const projectColumnClickable = (
  row: ActualWorkingHoursRow,
  projectUuid: string | undefined
): boolean => {
  return (
    row.type === 'WORK_HOUR' &&
    !!row.projectHours &&
    row.projectHours.some(p => p.projectUuid === projectUuid)
  )
}

const totalActualHourColumnClickable = (row: ActualWorkingHoursRow) => {
  if (row.type !== 'WORK_HOUR') return false
  return !!row.totalActualHour && row.totalActualHour >= 0.01
}

const getActualWorkColumnCellStyle = (clickable: () => boolean) => {
  const style: CellStyle = {
    justifyContent: 'center',
  }
  if (!clickable()) return style

  style.cursor = 'pointer'
  style.color = Color.MAIN
  return style
}

const onActualWorkCellClicked = (
  event: CellClickedEvent,
  userUuid: string | undefined,
  openTaskActualWorkList?: (props: openTaskActualWorkListProps) => void,
  crossProject?: boolean
) => {
  if (!openTaskActualWorkList || !(event.event instanceof PointerEvent)) {
    return
  }
  const row: ActualWorkingHoursRow = event.data
  const projectUuid = event.context.projectUuid ?? event.colDef.refData?.uuid
  const projectCode = event.context.projectCode ?? event.colDef.refData?.code
  openTaskActualWorkList({
    userUuid,
    projectUuid: crossProject ? undefined : projectUuid,
    projectCode: crossProject ? undefined : projectCode,
    targetDate: row.date,
    event: event.event,
  })
}

const parseTimeFormat = (str: string) => {
  str = str.replace('：', ':')
  str = str.replace(/[０-９]/g, s => {
    return String.fromCharCode(s.charCodeAt(0) - 65248)
  })
  if (str.includes(':')) {
    return str
  }
  if (str.length === 4) {
    return `${str.substring(0, 2)}:${str.substring(2)}`
  }
  if (str.length === 3) {
    return `${str.substring(0, 1)}:${str.substring(1)}`
  }
  return undefined
}
